import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  ShamRatingStarIcon,
  ShamArrowDown01StrokeIcon,
  ShamFi_chevronDownIcon,
} from 'sham-components/icons';
import { LanguageService } from 'sham-components/services';
import {
  PublicSellerReviewDto,
  PublicSellerReviewsSummaryDto,
  SellerProfileApi,
} from '@/app/services/seller-profile-api';

interface SellerReviewItem {
  id: string;
  customerDisplayName: string;
  rating: number;
  text: string;
  createdAtUtc: string;
  avatarUrl: string | null;
}

interface RatingBreakdownItem {
  stars: number;
  count: number;
  percentage: number;
}

@Component({
  selector: 'app-seller-reviews',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    TranslateModule,
    ShamRatingStarIcon,
    ShamArrowDown01StrokeIcon,
    ShamFi_chevronDownIcon,
  ],
  templateUrl: './seller-reviews.html',
})
export class SellerReviews implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly sellerProfileApi = inject(SellerProfileApi);
  private readonly languageService = inject(LanguageService);

  readonly isLoading = signal(true);
  readonly sellerName = signal('');
  readonly selectedFilter = signal<number | null>(null);
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly totalCount = signal(0);
  readonly showFilterDropdown = signal(false);
  readonly showPageDropdown = signal(false);
  readonly stars = [1, 2, 3, 4, 5];
  readonly allReviews = signal<SellerReviewItem[]>([]);
  readonly sellerId = signal<string>('');
  readonly reviewSummary = signal<PublicSellerReviewsSummaryDto | null>(null);

  readonly filteredReviews = computed(() => {
    const rating = this.selectedFilter();
    const reviews = this.allReviews();
    if (!rating) {
      return reviews;
    }

    return reviews.filter((review) => Math.round(review.rating) === rating);
  });

  readonly averageRating = computed(() => {
    return this.reviewSummary()?.averageRating ?? 0;
  });

  readonly totalReviews = computed(
    () => this.reviewSummary()?.approvedPublicReviewCount ?? 0,
  );
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalCount() / this.pageSize())),
  );
  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, index) => index + 1),
  );

  readonly pagedReviews = computed(() => {
    return this.filteredReviews();
  });

  readonly ratingBreakdown = computed<RatingBreakdownItem[]>(() => {
    const summary = this.reviewSummary();
    const total = summary?.approvedPublicReviewCount ?? 0;
    const counts = {
      5: summary?.rating5Count ?? 0,
      4: summary?.rating4Count ?? 0,
      3: summary?.rating3Count ?? 0,
      2: summary?.rating2Count ?? 0,
      1: summary?.rating1Count ?? 0,
    };

    return [5, 4, 3, 2, 1].map((stars) => {
      const count = counts[stars as keyof typeof counts];
      return {
        stars,
        count,
        percentage: total ? Math.round((count / total) * 100) : 0,
      };
    });
  });

  ngOnInit(): void {
    void this.initialize();
  }

  async initialize() {
    const sellerId = this.route.snapshot.paramMap.get('sellerId');

    if (!sellerId) {
      this.sellerName.set('Store');
      this.allReviews.set([]);
      this.reviewSummary.set(null);
      this.isLoading.set(false);
      return;
    }
    this.sellerId.set(sellerId);

    try {
      const [store, summary] = await Promise.all([
        firstValueFrom(this.sellerProfileApi.getStoreInfo(sellerId)),
        firstValueFrom(this.sellerProfileApi.getSellerReviewsSummary(sellerId)),
      ]);

      this.sellerName.set(store.storeName);
      this.reviewSummary.set(summary);
      await this.loadReviewsPage(1);
    } catch (error) {
      console.error('Failed to load seller store info:', error);
      this.sellerName.set('Store');
      this.allReviews.set([]);
      this.reviewSummary.set(null);
    } finally {
      this.isLoading.set(false);
    }
  }

  isStarFilled(rating: number, star: number): boolean {
    return star <= Math.round(Math.max(0, Math.min(5, rating)));
  }

  getInitials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .map((part) => part[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  formatDate(date: string): string {
    const locale = this.languageService.currentLang() === 'ar' ? 'ar-SY' : 'en-US';
    return new Date(date).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  filterByRating(rating: number | null) {
    this.selectedFilter.set(rating);
    this.showFilterDropdown.set(false);
  }

  async goToPage(page: number) {
    if (page < 1 || page > this.totalPages()) {
      return;
    }

    await this.loadReviewsPage(page);
    this.showPageDropdown.set(false);
  }

  goToPreviousPage() {
    if (this.currentPage() > 1) {
      void this.goToPage(this.currentPage() - 1);
    }
  }

  goToNextPage() {
    if (this.currentPage() < this.totalPages()) {
      void this.goToPage(this.currentPage() + 1);
    }
  }

  trackByReview(index: number, review: SellerReviewItem): string {
    return review.id;
  }

  private async loadReviewsPage(page: number) {
    if (!this.sellerId()) {
      return;
    }

    this.isLoading.set(true);

    try {
      const response = await firstValueFrom(
        this.sellerProfileApi.getSellerReviews({
          sellerId: this.sellerId(),
          page,
          pageSize: this.pageSize(),
        }),
      );

      this.allReviews.set(response.items.map((review) => this.mapReview(review)));
      this.currentPage.set(response.page);
      this.pageSize.set(response.pageSize);
      this.totalCount.set(response.totalCount);
    } catch (error) {
      console.error('Failed to load seller reviews:', error);
      this.allReviews.set([]);
      this.currentPage.set(1);
      this.totalCount.set(0);
    } finally {
      this.isLoading.set(false);
    }
  }

  private mapReview(review: PublicSellerReviewDto): SellerReviewItem {
    return {
      id: review.storeReviewId,
      customerDisplayName: review.customerDisplayName,
      rating: review.rating,
      text: review.text ?? '',
      createdAtUtc: review.createdAtUtc,
      avatarUrl: null,
    };
  }
}
